// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//
// If you have dependencies that try to import CSS, esbuild will generate a separate `app.css` file.
// To load it, simply add a second `<link>` to your `root.html.heex` file.

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import { hooks as colocatedHooks } from "phoenix-colocated/doc_gather";
import topbar from "../vendor/topbar";
import Sortable from "../vendor/sortable";
import PdfViewer from "./hooks/pdf_viewer";
import PdfFieldPlacer from "./hooks/pdf_field_placer";
import PdfFormFiller from "./hooks/pdf_form_filler";
import PdfSignaturePad from "./hooks/signature_pad";
import SignaturePad from "./hooks/form_signature_pad";
import NativeFormSubmit from "./hooks/native_form_submit";
import Confetti from "./hooks/confetti";
import InAppBrowserWarning from "./hooks/in_app_browser";
import CameraPermissionHint from "./hooks/camera_permission_hint";
import ItemPageUpload from "./hooks/item_page_upload";
import PhotoQualityCheck from "./hooks/photo_quality_check";
import AutoDismissFlash from "./hooks/auto_dismiss_flash";

// Custom hooks for LiveView
const Hooks = {
  ...colocatedHooks,
  Confetti,
  PdfViewer,
  PdfFieldPlacer,
  PdfFormFiller,
  PdfSignaturePad,
  SignaturePad,
  NativeFormSubmit,
  InAppBrowserWarning,
  CameraPermissionHint,
  ItemPageUpload,
  PhotoQualityCheck,
  AutoDismissFlash,

  // Renders a Cloudflare Turnstile widget inside a LiveView form. The widget
  // is rendered explicitly (not via the auto-scan) so it survives LiveView
  // patches; the element is marked phx-update="ignore" by the template.
  Turnstile: {
    mounted() {
      const render = () => {
        if (!window.turnstile) return setTimeout(render, 100);
        this.widgetId = window.turnstile.render(this.el, {
          sitekey: this.el.dataset.sitekey,
          theme: "auto",
        });
      };
      render();
      this.handleEvent("turnstile_reset", () => {
        if (window.turnstile && this.widgetId) window.turnstile.reset(this.widgetId);
      });
    },
    destroyed() {
      if (window.turnstile && this.widgetId) window.turnstile.remove(this.widgetId);
    },
  },

  // Writes pushed copy_to_clipboard text and reports the outcome back to the
  // LiveView so the flash reflects whether the copy actually happened
  ClipboardFlash: {
    mounted() {
      this.handler = (event) => {
        const { text, flash } = event.detail;
        navigator.clipboard
          .writeText(text)
          .then(() => this.pushEvent("clipboard_result", { ok: true, flash }))
          .catch(() => this.pushEvent("clipboard_result", { ok: false }));
      };
      window.addEventListener("phx:copy_to_clipboard", this.handler);
    },
    destroyed() {
      window.removeEventListener("phx:copy_to_clipboard", this.handler);
    },
  },

  // Syncs form field focus/hover with PdfViewer field highlighting (bidirectional)
  // Also forwards text input values for live preview on the PDF
  PdfFieldSync: {
    mounted() {
      this._activeField = null
      this._hoveredField = null

      // Focus on right → highlight on left
      this.el.addEventListener("focusin", (e) => {
        const fieldName = e.target.closest("[data-pdf-field]")?.dataset.pdfField
        if (fieldName) {
          this._activeField = fieldName
          window.dispatchEvent(
            new CustomEvent("pdf-highlight-field", { detail: { name: fieldName, sticky: true } }),
          )
        }
      })

      this.el.addEventListener("focusout", () => {
        this._activeField = null
        window.dispatchEvent(new CustomEvent("pdf-clear-highlight"))
      })

      // Text/select input changes → live preview on PDF
      this.el.addEventListener("input", (e) => {
        const wrapper = e.target.closest("[data-pdf-field]")
        if (!wrapper) return
        const fieldName = wrapper.dataset.pdfField
        const isCheckbox = e.target.type === "checkbox"
        const value = isCheckbox ? (e.target.checked ? "Yes" : "") : e.target.value
        const type = isCheckbox ? "checkbox" : "text"
        window.dispatchEvent(
          new CustomEvent("pdf-field-value-changed", {
            detail: { name: fieldName, value, type },
          }),
        )
      })

      this.el.addEventListener("change", (e) => {
        const wrapper = e.target.closest("[data-pdf-field]")
        if (!wrapper) return
        const fieldName = wrapper.dataset.pdfField
        if (e.target.tagName === "SELECT") {
          window.dispatchEvent(
            new CustomEvent("pdf-field-value-changed", {
              detail: { name: fieldName, value: e.target.value, type: "text" },
            }),
          )
        }
      })

      // Hover on right → highlight on left (only when no field is focused)
      // Uses mouseover/mouseout with dedup to avoid redundant dispatches
      this.el.addEventListener("mouseover", (e) => {
        if (this._activeField) return
        const fieldName = e.target.closest("[data-pdf-field]")?.dataset.pdfField || null
        if (fieldName === this._hoveredField) return
        this._hoveredField = fieldName
        if (fieldName) {
          window.dispatchEvent(
            new CustomEvent("pdf-highlight-field", { detail: { name: fieldName, sticky: false } }),
          )
        }
      })

      this.el.addEventListener("mouseout", (e) => {
        if (this._activeField) return
        const fieldName = e.target.closest("[data-pdf-field]")?.dataset.pdfField
        if (!fieldName || fieldName !== this._hoveredField) return
        // Check if we're moving to another element within the same field
        const related = e.relatedTarget?.closest?.("[data-pdf-field]")
        if (related?.dataset.pdfField === fieldName) return
        this._hoveredField = null
        window.dispatchEvent(new CustomEvent("pdf-clear-highlight"))
      })

      // Listen for hover on left PDF overlay → highlight form field on right
      this._onPdfFieldHover = (e) => {
        const { name, active } = e.detail
        const wrapper = this.el.querySelector(`[data-pdf-field="${name}"]`)
        if (!wrapper) return
        if (active) {
          wrapper.classList.add("ring-2", "ring-success/50", "rounded-lg", "bg-success/5")
        } else {
          wrapper.classList.remove("ring-2", "ring-success/50", "rounded-lg", "bg-success/5")
        }
      }
      window.addEventListener("pdf-form-field-hover", this._onPdfFieldHover)
    },
    destroyed() {
      window.removeEventListener("pdf-form-field-hover", this._onPdfFieldHover)
    },
  },

  // ScrollSpy for API docs sidebar highlighting
  ScrollSpy: {
    mounted() {
      this._observeAll();
    },
    updated() {
      // Only re-observe if the set of sections changed (e.g. after search filter)
      const current = new Set(
        [...this.el.querySelectorAll("[data-section]")].map((el) => el.id),
      );
      if (
        !this._sectionIds ||
        current.size !== this._sectionIds.size ||
        [...current].some((id) => !this._sectionIds.has(id))
      ) {
        this._observeAll();
      }
    },
    destroyed() {
      this._observer?.disconnect();
    },
    _observeAll() {
      this._observer?.disconnect();
      this._observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.pushEvent("section_visible", { id: entry.target.id });
              break;
            }
          }
        },
        { rootMargin: "-80px 0px -80% 0px" },
      );
      const sections = this.el.querySelectorAll("[data-section]");
      this._sectionIds = new Set([...sections].map((el) => el.id));
      sections.forEach((el) => this._observer.observe(el));
    },
  },

  // Sends document.referrer to the LiveView once after the websocket connects.
  // Used by the client portal to attribute where viewers came from.
  PortalReferrer: {
    mounted() {
      const ref = document.referrer || "";
      if (ref) {
        this.pushEvent("portal_referrer", { referrer: ref });
      }
    },
  },

  // Sortable.js hook for drag-drop reordering
  Sortable: {
    mounted() {
      this.sortable = Sortable.create(this.el, {
        animation: 150,
        handle: this.el.dataset.handle || null,
        ghostClass: "opacity-40",
        dragClass: "shadow-lg",
        onEnd: (evt) => {
          const ids = [...this.el.children]
            .map((el) => el.dataset.id)
            .filter(Boolean);
          this.pushEvent("reorder", { ids });
        },
      });
    },
    updated() {
      // Re-initialize if the list changes
      // This ensures new items are draggable
    },
    destroyed() {
      if (this.sortable) {
        this.sortable.destroy();
      }
    },
  },
};

const csrfToken = document
  .querySelector("meta[name='csrf-token']")
  .getAttribute("content");
const liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 2500,
  params: {
    _csrf_token: csrfToken,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
  hooks: Hooks,
});

// Show progress bar on live navigation and form submits
topbar.config({ barColors: { 0: "#29d" }, shadowColor: "rgba(0, 0, 0, .3)" });
window.addEventListener("phx:page-loading-start", (_info) => topbar.show(300));
window.addEventListener("phx:page-loading-stop", (_info) => topbar.hide());

// connect if there are any LiveViews on the page
liveSocket.connect();

// Scroll a server-named element into view (validation errors, newly added
// builder fields). Focuses the first focusable control inside it, if any.
window.addEventListener("phx:scroll_to", (event) => {
  const el = document.getElementById(event.detail.id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  const focusable = el.querySelector("input, textarea, select");
  if (focusable) focusable.focus({ preventScroll: true });
});

// Start a file download the server says is ready (e.g. a clean copy the
// user asked for finished generating). Content-Disposition: attachment keeps
// the LiveView page in place.
window.addEventListener("phx:download", (event) => {
  window.location = event.detail.url;
});

// Handle gtag analytics events from LiveView
window.addEventListener("phx:gtag", (event) => {
  const { name, params, conversion_id } = event.detail;
  console.log("[Analytics] Firing gtag event:", name, params);

  if (typeof gtag !== "undefined") {
    gtag("event", name, params);

    if (conversion_id) {
      gtag("event", "conversion", { send_to: conversion_id });
    }
  }
});

// Fire a gtag event when any element with data-track is clicked.
// Optional data-track-context becomes the event's `context` param.
document.addEventListener("click", (e) => {
  const el = e.target.closest("[data-track]");
  if (!el || typeof gtag === "undefined") return;
  const params = el.dataset.trackContext ? { context: el.dataset.trackContext } : {};
  gtag("event", el.dataset.track, params);
});

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;

// The lines below enable quality of life phoenix_live_reload
// development features:
//
//     1. stream server logs to the browser console
//     2. click on elements to jump to their definitions in your code editor
//
if (process.env.NODE_ENV === "development") {
  window.addEventListener(
    "phx:live_reload:attached",
    ({ detail: reloader }) => {
      // Enable server log streaming to client.
      // Disable with reloader.disableServerLogs()
      reloader.enableServerLogs();

      // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
      //
      //   * click with "c" key pressed to open at caller location
      //   * click with "d" key pressed to open at function component definition location
      let keyDown;
      window.addEventListener("keydown", (e) => (keyDown = e.key));
      window.addEventListener("keyup", (e) => (keyDown = null));
      window.addEventListener(
        "click",
        (e) => {
          if (keyDown === "c") {
            e.preventDefault();
            e.stopImmediatePropagation();
            reloader.openEditorAtCaller(e.target);
          } else if (keyDown === "d") {
            e.preventDefault();
            e.stopImmediatePropagation();
            reloader.openEditorAtDef(e.target);
          }
        },
        true,
      );

      window.liveReloader = reloader;
    },
  );
}

// =============================================================================
// Scroll-triggered animations
// Add 'animate-on-scroll' class to elements you want to animate when visible
// =============================================================================
const initScrollAnimations = () => {
  // All animation class selectors that should trigger on scroll
  const animationSelectors = [
    ".animate-on-scroll",
    ".animate-slide-stagger",
    ".animate-badge-pop",
    ".animate-progress",
  ];
  const allSelector = animationSelectors.join(", ");

  // Respect reduced motion preferences
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    document.querySelectorAll(allSelector).forEach((el) => {
      el.classList.remove(
        "animate-on-scroll",
        "animate-slide-stagger",
        "animate-badge-pop",
        "animate-progress",
      );
      el.style.opacity = "1";
      // Set progress bars to full width
      if (el.classList.contains("animate-progress")) {
        el.style.width =
          el.style.getPropertyValue("--progress-target") || "100%";
      }
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target); // Only animate once
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px", // Trigger slightly before element is fully visible
    },
  );

  document.querySelectorAll(allSelector).forEach((el) => {
    observer.observe(el);
  });
};

// Run on initial page load
document.addEventListener("DOMContentLoaded", initScrollAnimations);

// Re-run after LiveView navigation
window.addEventListener("phx:page-loading-stop", () => {
  // Small delay to ensure DOM is updated
  setTimeout(initScrollAnimations, 100);
});

// =============================================================================
// Home page product video
// Picks the landscape or portrait render for the viewport, loads it only when
// scrolled near, and plays it only while visible. Reduced-motion visitors get
// the poster plus native controls instead of autoplay.
// =============================================================================
const initIntakeVideo = () => {
  const video = document.getElementById("intake-video");
  if (!video || video.__intakeBound) return;
  video.__intakeBound = true;

  const phone = window.matchMedia("(max-width: 639px)");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let inView = false;
  const url = (ext) => `/videos/intake-demo-${phone.matches ? "portrait" : "landscape"}.${ext}`;

  const load = () => {
    video.src = url("mp4");
    if (inView && !reducedMotion) video.play().catch(() => {});
  };

  video.poster = url("jpg");

  // Crossing the breakpoint swaps to the other render (the box's aspect changes too).
  phone.addEventListener("change", () => {
    video.poster = url("jpg");
    if (video.src) load();
  });

  if (reducedMotion) {
    video.controls = true;
    load();
    return;
  }

  new IntersectionObserver(
    ([entry]) => {
      inView = entry.isIntersecting;
      if (inView && !video.src) load();
      else if (inView) video.play().catch(() => {});
      else video.pause();
    },
    { threshold: 0.25 },
  ).observe(video);
};

document.addEventListener("DOMContentLoaded", initIntakeVideo);
window.addEventListener("phx:page-loading-stop", () => {
  setTimeout(initIntakeVideo, 100);
});
